/**
 * Theme configuration
 * 
 * This file defines the default themes and provides utilities for creating custom themes.
 * You can easily customize existing themes or create completely new ones.
 */

export type ThemeColors = {
  // Base
  background: string;
  foreground: string;
  
  // Component colors
  card: string;
  cardForeground: string;
  popover: string;
  popoverForeground: string;
  
  // Primary and accent colors
  primary: string;
  primaryForeground: string;
  secondary: string;
  secondaryForeground: string;
  accent: string;
  accentForeground: string;
  muted: string;
  mutedForeground: string;
  
  // UI elements
  border: string;
  input: string;
  ring: string;
  destructive: string;
  
  // Header and footer specific colors
  header: string;
  headerForeground: string;
  footer: string;
  footerForeground: string;
  
  // Sidebar specific colors
  sidebar: string;
  sidebarForeground: string;
  sidebarBorder: string;
  
  // Chart colors
  chart1: string;
  chart2: string;
  chart3: string;
  chart4: string;
  chart5: string;
};

// Default light theme
export const lightTheme: ThemeColors = {
  // Base
  background: 'oklch(1 0 0)',
  foreground: 'oklch(0.145 0 0)',
  
  // Component colors
  card: 'oklch(1 0 0)',
  cardForeground: 'oklch(0.145 0 0)',
  popover: 'oklch(1 0 0)',
  popoverForeground: 'oklch(0.145 0 0)',
  
  // Primary and accent colors
  primary: 'oklch(0.205 0 0)',
  primaryForeground: 'oklch(0.985 0 0)',
  secondary: 'oklch(0.97 0 0)',
  secondaryForeground: 'oklch(0.205 0 0)',
  accent: 'oklch(0.97 0 0)',
  accentForeground: 'oklch(0.205 0 0)',
  muted: 'oklch(0.97 0 0)',
  mutedForeground: 'oklch(0.556 0 0)',
  
  // UI elements
  border: 'oklch(0.922 0 0)',
  input: 'oklch(0.922 0 0)',
  ring: 'oklch(0.708 0 0)',
  destructive: 'oklch(0.577 0.245 27.325)',
  
  // Header and footer specific colors
  header: 'oklch(0.97 0 0)',
  headerForeground: 'oklch(0.205 0 0)',
  footer: 'oklch(0.97 0 0)',
  footerForeground: 'oklch(0.205 0 0)',
  
  // Sidebar specific colors
  sidebar: 'oklch(0.985 0 0)',
  sidebarForeground: 'oklch(0.145 0 0)',
  sidebarBorder: 'oklch(0.922 0 0)',
  
  // Chart colors
  chart1: 'oklch(0.646 0.222 41.116)',
  chart2: 'oklch(0.6 0.118 184.704)',
  chart3: 'oklch(0.398 0.07 227.392)',
  chart4: 'oklch(0.828 0.189 84.429)',
  chart5: 'oklch(0.769 0.188 70.08)',
};

// Default dark theme
export const darkTheme: ThemeColors = {
  // Base
  background: 'oklch(0.145 0 0)',
  foreground: 'oklch(0.985 0 0)',
  
  // Component colors
  card: 'oklch(0.205 0 0)',
  cardForeground: 'oklch(0.985 0 0)',
  popover: 'oklch(0.205 0 0)',
  popoverForeground: 'oklch(0.985 0 0)',
  
  // Primary and accent colors
  primary: 'oklch(0.922 0 0)',
  primaryForeground: 'oklch(0.205 0 0)',
  secondary: 'oklch(0.269 0 0)',
  secondaryForeground: 'oklch(0.985 0 0)',
  accent: 'oklch(0.269 0 0)',
  accentForeground: 'oklch(0.985 0 0)',
  muted: 'oklch(0.269 0 0)',
  mutedForeground: 'oklch(0.708 0 0)',
  
  // UI elements
  border: 'oklch(1 0 0 / 10%)',
  input: 'oklch(1 0 0 / 15%)',
  ring: 'oklch(0.556 0 0)',
  destructive: 'oklch(0.704 0.191 22.216)',
  
  // Header and footer specific colors
  header: 'oklch(0.205 0 0)',
  headerForeground: 'oklch(0.985 0 0)',
  footer: 'oklch(0.205 0 0)',
  footerForeground: 'oklch(0.985 0 0)',
  
  // Sidebar specific colors
  sidebar: 'oklch(0.205 0 0)',
  sidebarForeground: 'oklch(0.985 0 0)',
  sidebarBorder: 'oklch(1 0 0 / 10%)',
  
  // Chart colors
  chart1: 'oklch(0.488 0.243 264.376)',
  chart2: 'oklch(0.696 0.17 162.48)',
  chart3: 'oklch(0.769 0.188 70.08)',
  chart4: 'oklch(0.627 0.265 303.9)',
  chart5: 'oklch(0.645 0.246 16.439)',
};

// Custom theme presets that can be used out of the box
export const themePresets = {
  // Solarized theme: light variant
  solarizedLight: {
    ...lightTheme,
    background: 'oklch(0.99 0.03 107)',
    foreground: 'oklch(0.2 0.05 248)',
    primary: 'oklch(0.53 0.18 12.2)',
    secondary: 'oklch(0.58 0.1 196.6)',
    accent: 'oklch(0.48 0.1 183.6)',
    border: 'oklch(0.84 0.04 106)',
    header: 'oklch(0.91 0.05 107)',
    footer: 'oklch(0.91 0.05 107)',
  },
  
  // Solarized theme: dark variant
  solarizedDark: {
    ...darkTheme,
    background: 'oklch(0.22 0.05 248)',
    foreground: 'oklch(0.91 0.03 107)',
    primary: 'oklch(0.58 0.16 12.2)',
    secondary: 'oklch(0.65 0.12 196.6)',
    accent: 'oklch(0.55 0.15 183.6)',
    border: 'oklch(0.3 0.05 248)',
    header: 'oklch(0.17 0.05 248)',
    footer: 'oklch(0.17 0.05 248)',
  },
  
  // Nord theme: light variant
  nordLight: {
    ...lightTheme,
    background: 'oklch(0.97 0.01 240)',
    foreground: 'oklch(0.26 0.04 248)',
    primary: 'oklch(0.54 0.13 206)',
    secondary: 'oklch(0.7 0.08 250)',
    accent: 'oklch(0.64 0.1 148)',
    border: 'oklch(0.84 0.02 240)',
    header: 'oklch(0.9 0.02 240)',
    footer: 'oklch(0.9 0.02 240)',
  },
  
  // Nord theme: dark variant
  nordDark: {
    ...darkTheme,
    background: 'oklch(0.26 0.04 248)',
    foreground: 'oklch(0.97 0.01 240)',
    primary: 'oklch(0.64 0.13 206)',
    secondary: 'oklch(0.5 0.08 250)',
    accent: 'oklch(0.74 0.1 148)',
    border: 'oklch(0.33 0.03 248)',
    header: 'oklch(0.2 0.04 248)',
    footer: 'oklch(0.2 0.04 248)',
  },
  
  // Berry Babies theme: light variant
  berryLight: {
    ...lightTheme,
    // Green/yellow/purple color scheme
    background: 'oklch(0.99 0.02 120)',
    foreground: 'oklch(0.2 0.05 280)',
    primary: 'oklch(0.55 0.2 280)',  // Purple
    secondary: 'oklch(0.7 0.15 120)', // Green
    accent: 'oklch(0.85 0.15 90)',   // Yellow
    border: 'oklch(0.9 0.05 120)',
    // Apply the same colors to header/footer
    header: 'oklch(0.55 0.2 280)',   // Purple header
    headerForeground: 'oklch(0.99 0.01 0)',
    footer: 'oklch(0.55 0.2 280)',   // Purple footer
    footerForeground: 'oklch(0.99 0.01 0)',
    // Chart colors
    chart1: 'oklch(0.7 0.15 120)',  // Green
    chart2: 'oklch(0.85 0.15 90)',  // Yellow
    chart3: 'oklch(0.65 0.25 300)', // Purple
    chart4: 'oklch(0.6 0.2 150)',   // Green variant
    chart5: 'oklch(0.75 0.15 80)',  // Yellow variant
  },
  
  // Berry Babies theme: dark variant
  berryDark: {
    ...darkTheme,
    // Green/yellow/purple color scheme
    background: 'oklch(0.18 0.04 280)',
    foreground: 'oklch(0.95 0.02 120)',
    primary: 'oklch(0.65 0.2 280)',  // Purple
    secondary: 'oklch(0.55 0.15 120)', // Green
    accent: 'oklch(0.75 0.15 90)',   // Yellow
    border: 'oklch(0.3 0.1 280)',
    // Apply the same colors to header/footer
    header: 'oklch(0.25 0.1 280)',   // Purple header
    headerForeground: 'oklch(0.99 0.01 0)',
    footer: 'oklch(0.25 0.1 280)',   // Purple footer
    footerForeground: 'oklch(0.99 0.01 0)',
    // Chart colors
    chart1: 'oklch(0.6 0.15 120)',  // Green
    chart2: 'oklch(0.75 0.15 90)',  // Yellow
    chart3: 'oklch(0.7 0.25 300)',  // Purple
    chart4: 'oklch(0.5 0.2 150)',   // Green variant
    chart5: 'oklch(0.65 0.15 80)',  // Yellow variant
  }
};

/**
 * Create a custom theme by extending an existing theme
 * 
 * @param baseTheme The base theme to extend (light, dark, or a custom theme)
 * @param customColors Custom color overrides
 * @returns A new theme with the custom colors
 */
export function createTheme(baseTheme: ThemeColors, customColors: Partial<ThemeColors>): ThemeColors {
  return {
    ...baseTheme,
    ...customColors,
  };
}

/**
 * Convert theme colors to CSS variables
 * 
 * @param theme The theme to convert
 * @returns CSS variables object
 */
export function themeToCssVars(theme: ThemeColors): Record<string, string> {
  return {
    '--background': theme.background,
    '--foreground': theme.foreground,
    '--card': theme.card,
    '--card-foreground': theme.cardForeground,
    '--popover': theme.popover,
    '--popover-foreground': theme.popoverForeground,
    '--primary': theme.primary,
    '--primary-foreground': theme.primaryForeground,
    '--secondary': theme.secondary,
    '--secondary-foreground': theme.secondaryForeground,
    '--accent': theme.accent,
    '--accent-foreground': theme.accentForeground,
    '--muted': theme.muted,
    '--muted-foreground': theme.mutedForeground,
    '--border': theme.border,
    '--input': theme.input,
    '--ring': theme.ring,
    '--destructive': theme.destructive,
    '--header': theme.header,
    '--header-foreground': theme.headerForeground,
    '--footer': theme.footer,
    '--footer-foreground': theme.footerForeground,
    '--sidebar': theme.sidebar,
    '--sidebar-foreground': theme.sidebarForeground,
    '--sidebar-border': theme.sidebarBorder,
    '--chart-1': theme.chart1,
    '--chart-2': theme.chart2,
    '--chart-3': theme.chart3,
    '--chart-4': theme.chart4,
    '--chart-5': theme.chart5,
  };
}